<template>
  <div v-if="hasAccess" class="space-y-6 pb-10">
    <page-content>
      <section class="space-y-6">
        <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <dashboard-date-filters
            class="xl:flex-1"
            :quick-ranges="quickRanges"
            :active-range-id="activeQuickRangeId"
            :date-range="dateRange"
            :max-date="maxDate"
            @select-range="applyQuickRange"
            @show-full="showFull"
            @update:date-range="handleDateRangeUpdate"
          />

          <div class="tracking-filters inline-flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm xl:min-w-[360px]">
            <div class="tracking-filter-field tracking-filter-field--wide">
              <t-rich-select
                :key="domainSelectKey"
                class="tracking-filter-control"
                multiple
                :close-on-select="false"
                :options="domainOptions"
                value-attribute="uuid"
                text-attribute="label"
                placeholder="Tous les domaines"
                v-model="selectedDomainUuids"
              >
                <template slot="label" slot-scope="{ options, className }">
                  <span :class="className">
                    {{ domainSelectionLabel }}
                  </span>
                </template>
              </t-rich-select>
            </div>
          </div>
        </div>

        <section class="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <header class="email-space-header">
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Campagnes email</h2>
                <p class="mt-1 text-sm text-gray-500">Suivi des campagnes de téléchargement envoyées et programmées.</p>
              </div>
              <p v-if="isLoading" class="text-sm text-gray-500">Chargement des données…</p>
            </div>
          </header>
        </section>

        <div class="email-overview-grid">
          <article class="email-stat-card email-stat-card--sent">
            <span>Envoyées période</span>
            <strong>{{ formatNumber(summary.sentCampaigns) }}</strong>
          </article>
          <article class="email-stat-card email-stat-card--scheduled">
            <span>Programmées</span>
            <strong>{{ formatNumber(summary.scheduledCampaigns) }}</strong>
          </article>
          <article class="email-stat-card email-stat-card--recipients">
            <span>Clics stores période</span>
            <strong>{{ formatNumber(summary.storeClicks) }}</strong>
          </article>
          <article class="email-stat-card email-stat-card--clicks">
            <span>Création de compte période</span>
            <strong>{{ formatNumber(summary.accountCreations) }}</strong>
          </article>
        </div>

        <section class="email-space email-space--sent rounded-2xl border border-gray-200 bg-white shadow-sm">
          <header class="email-space-header">
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Campagnes envoyées et programmées</h2>
                <p class="mt-1 text-sm text-gray-500">Historique filtré par période et domaine, avec les envois prévus.</p>
              </div>
              <div class="email-status-filter" aria-label="Filtrer les campagnes par statut">
                <button
                  v-for="option in campaignStatusOptions"
                  :key="option.value"
                  type="button"
                  :class="{ 'is-active': campaignStatusFilter === option.value }"
                  @click="campaignStatusFilter = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </header>

          <div class="px-2 py-4">
            <m-v-p-table
              v-if="gridOptions"
              :options="gridOptions"
              :grid-data="gridData"
              status-bar-type="displayed"
              @gridReady="onGridReady"
            />
          </div>
        </section>
      </section>
    </page-content>
  </div>

  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-500">Vous n’avez pas accès à cette section.</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { http } from '@/services/api.service'
import { format, parseISO, subDays, subMonths, subWeeks } from 'date-fns'
import fr from 'date-fns/locale/fr'
import PageContent from '@/components/PageContent.vue'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import DashboardDateFilters from '@/components/dashboard/DashboardDateFilters.vue'
import EmailCampaignRowDetail from './EmailCampaignRowDetail.vue'
import { DASHBOARD_QUICK_RANGES } from './state'
import { getFullTrackingDateRange } from './domainPeriod'
import { fetchTrackingDomains } from './trackingDomains'
import { formatTrackingDomainSelectionLabel, getTrackingQueryState, normalizeTrackingDomainUuids, syncTrackingQueryFromVm } from './trackingQuery'

const DATE_FORMAT = 'yyyy-MM-dd'

function toIsoDate (date) {
  return format(date, DATE_FORMAT)
}

function numericComparator (valueA, valueB) {
  return Number(valueA || 0) - Number(valueB || 0)
}

function dateComparator (valueA, valueB) {
  if (!valueA && !valueB) return 0
  if (!valueA) return -1
  if (!valueB) return 1

  try {
    return parseISO(valueA).getTime() - parseISO(valueB).getTime()
  } catch (error) {
    return String(valueA).localeCompare(String(valueB))
  }
}

export default {
  name: 'app-dashboard-tracking-email-campaigns',
  components: {
    PageContent,
    MVPTable,
    DashboardDateFilters,
    EmailCampaignRowDetail // eslint-disable-line
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      const hasAccess = await vm.ensureAccess()
      if (!hasAccess) {
        vm.$router.replace('/app/dashboard')
      }
    })
  },
  data () {
    const today = new Date()
    const defaultEnd = toIsoDate(today)
    const defaultStart = toIsoDate(subDays(today, 29))
    const trackingState = getTrackingQueryState(this.$route.query, {
      activeQuickRangeId: 'month-1',
      dateRange: [defaultStart, defaultEnd],
      selectedDomainUuids: []
    })

    return {
      quickRanges: DASHBOARD_QUICK_RANGES.map(range => ({ ...range })),
      activeQuickRangeId: trackingState.activeQuickRangeId,
      dateRange: trackingState.dateRange,
      maxDate: defaultEnd,
      domainOptions: [],
      selectedDomainUuids: trackingState.selectedDomainUuids,
      summary: {
        sentCampaigns: 0,
        scheduledCampaigns: 0,
        storeClicks: 0,
        accountCreations: 0
      },
      campaignStatusFilter: 'all',
      campaignRows: [],
      gridOptions: null,
      gridData: [],
      gridApi: null,
      isLoading: false,
      campaignsRequestSequence: 0,
      activeCampaignsRequestKey: null,
      debouncedLoadCampaigns: null,
      isInitializingTracking: true
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.includes('admin') || this.roles.includes('community-manager')
    },
    campaignStatusOptions () {
      return [
        { value: 'all', label: 'All' },
        { value: 'scheduled', label: 'Programmées' },
        { value: 'sent', label: 'Envoyées' }
      ]
    },
    domainSelectionLabel () {
      return formatTrackingDomainSelectionLabel(this.selectedDomainUuids, this.domainOptions)
    },
    domainSelectKey () {
      return `${this.$route.path}::${this.domainOptions.length}`
    }
  },
  watch: {
    selectedDomainUuids: {
      handler () {
        if (this.activeQuickRangeId === 'full') {
          this.setFullDateRange()
        }
        syncTrackingQueryFromVm(this)
        if (this.isInitializingTracking) {
          return
        }
        this.scheduleCampaignsReload()
      },
      deep: true
    },
    campaignStatusFilter () {
      this.applyCampaignFilter()
    }
  },
  async created () {
    this.initializeGridOptions()
    this.debouncedLoadCampaigns = debounce(this.loadCampaigns, 400)

    const hasAccess = await this.ensureAccess()
    if (!hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }

    const waitForDomains = this.activeQuickRangeId === 'full'
    const domainsPromise = this.fetchDomains()
    if (waitForDomains) {
      await domainsPromise
      this.setFullDateRange()
    }
    syncTrackingQueryFromVm(this)
    this.isInitializingTracking = false
    this.loadCampaigns()
    if (!waitForDomains) {
      await domainsPromise
    }
  },
  beforeDestroy () {
    if (this.debouncedLoadCampaigns) {
      this.debouncedLoadCampaigns.cancel()
    }
    this.campaignsRequestSequence += 1
  },
  methods: {
    async ensureAccess () {
      if (!Array.isArray(this.roles) || this.roles.length === 0) {
        try {
          await this.$store.dispatch('FETCH_ROLES')
        } catch (error) {
          return false
        }
      }

      return this.hasAccess
    },
    initializeGridOptions () {
      this.gridOptions = {
        rowModelType: 'clientSide',
        masterDetail: true,
        detailCellRenderer: 'EmailCampaignRowDetail',
        detailRowAutoHeight: true,
        suppressMultiSort: true,
        defaultColDef: {
          resizable: true,
          sortable: true,
          floatingFilter: false,
          sortingOrder: ['asc', 'desc']
        },
        columnDefs: [
          {
            headerName: '',
            cellRenderer: 'agGroupCellRenderer',
            valueGetter: () => '',
            sortable: false,
            filter: false,
            suppressMenu: true,
            suppressSizeToFit: true,
            width: 64,
            minWidth: 64,
            maxWidth: 64,
            pinned: 'left'
          },
          {
            headerName: 'Titre de la campagne',
            field: 'title',
            minWidth: 260,
            pinned: 'left'
          },
          {
            headerName: 'Statut',
            field: 'status',
            minWidth: 130,
            valueFormatter: params => this.formatStatusLabel(params.value)
          },
          {
            headerName: 'Date d’envoi',
            field: 'campaign_date',
            minWidth: 160,
            comparator: dateComparator,
            sort: 'desc',
            valueFormatter: params => this.formatDateTime(params.value)
          },
          {
            headerName: 'Domaine',
            field: 'domain_label',
            minWidth: 170
          },
          {
            headerName: 'Destinataire',
            field: 'recipient_display',
            minWidth: 230
          },
          {
            headerName: 'Clics stores',
            field: 'store_clicks',
            minWidth: 140,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Créations compte (7j)',
            field: 'account_creations_7d',
            minWidth: 190,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          }
        ]
      }
    },
    async fetchDomains () {
      try {
        const domains = await fetchTrackingDomains()
        this.domainOptions = domains

        const allowedUuids = new Set(domains.map(domain => domain.uuid))
        const currentSelectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
        const nextSelectedDomainUuids = currentSelectedDomainUuids
          .filter(uuid => allowedUuids.has(uuid))

        if (nextSelectedDomainUuids.join('|') !== currentSelectedDomainUuids.join('|')) {
          this.selectedDomainUuids = nextSelectedDomainUuids
        }
      } catch (error) {
        this.domainOptions = []
      }
    },
    scheduleCampaignsReload () {
      if (this.debouncedLoadCampaigns) {
        this.debouncedLoadCampaigns()
        return
      }
      this.loadCampaigns()
    },
    loadCampaigns () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadCampaigns) {
        this.debouncedLoadCampaigns.cancel()
      }

      const params = this.buildRequestParams()
      const requestKey = JSON.stringify(params)
      if (this.activeCampaignsRequestKey === requestKey) {
        return
      }

      const requestSequence = this.campaignsRequestSequence + 1
      this.campaignsRequestSequence = requestSequence
      this.activeCampaignsRequestKey = requestKey
      this.isLoading = true

      http.get('/dashboard/email-campaigns', { params })
        .then(({ data }) => {
          if (requestSequence !== this.campaignsRequestSequence) {
            return
          }

          const rows = Array.isArray(data?.campaigns) ? data.campaigns : []
          const scheduledRows = Array.isArray(data?.scheduledCampaigns) ? data.scheduledCampaigns : []
          const summary = data?.summary || {}
          const sentCampaignRows = rows.map(row => this.normalizeCampaignRow(row))
          const scheduledCampaignRows = scheduledRows.map(row => this.normalizeCampaignRow(row))

          this.summary = {
            sentCampaigns: Number(summary.sentCampaigns || rows.length || 0),
            scheduledCampaigns: Number(summary.scheduledCampaigns || scheduledRows.length || 0),
            storeClicks: Number(summary.storeClicks || 0),
            accountCreations: Number(summary.accountCreations || 0)
          }
          this.campaignRows = [...scheduledCampaignRows, ...sentCampaignRows]
          this.applyCampaignFilter()
        })
        .catch(() => {
          if (requestSequence !== this.campaignsRequestSequence) {
            return
          }

          this.summary = {
            sentCampaigns: 0,
            scheduledCampaigns: 0,
            storeClicks: 0,
            accountCreations: 0
          }
          this.campaignRows = []
          this.gridData = []
          this.$buefy.toast.open({
            message: 'Impossible de charger les campagnes email.',
            type: 'is-danger'
          })
        })
        .finally(() => {
          if (requestSequence === this.campaignsRequestSequence) {
            this.isLoading = false
            this.activeCampaignsRequestKey = null
          }
        })
    },
    buildRequestParams () {
      const params = {}

      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        params.dateStart = this.dateRange[0]
        params.dateEnd = this.dateRange[1]
      }

      const selectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
      if (selectedDomainUuids.length > 0) {
        params.domains = selectedDomainUuids
      }

      return params
    },
    applyQuickRange (range) {
      if (!range) return

      this.activeQuickRangeId = range.id
      const today = new Date()
      let start = null

      if (range.type === 'week') {
        start = subWeeks(today, range.amount)
      } else if (range.type === 'month') {
        start = subMonths(today, range.amount)
      } else if (range.type === 'day') {
        start = subDays(today, range.amount - 1)
      }

      if (!start) {
        start = subDays(today, 29)
      }

      this.dateRange = [toIsoDate(start), toIsoDate(today)]
      syncTrackingQueryFromVm(this)
      this.loadCampaigns()
    },
    showFull () {
      this.activeQuickRangeId = 'full'
      this.setFullDateRange()
      syncTrackingQueryFromVm(this)
      this.loadCampaigns()
    },
    setFullDateRange () {
      this.dateRange = getFullTrackingDateRange(this.domainOptions, this.selectedDomainUuids)
      this.maxDate = this.dateRange[1]
    },
    handleDateRangeUpdate (range) {
      if (!Array.isArray(range) || range.length !== 2) {
        this.dateRange = null
      } else {
        this.dateRange = [...range]
      }

      this.activeQuickRangeId = null
      syncTrackingQueryFromVm(this)
      this.scheduleCampaignsReload()
    },
    onGridReady (params) {
      this.gridApi = params.api
    },
    normalizeCampaignRow (row) {
      const status = row?.status || 'sent'
      const campaignDate = status === 'scheduled'
        ? (row?.scheduled_at || row?.sent_at || null)
        : (row?.sent_at || row?.scheduled_at || null)

      return {
        ...row,
        campaign_date: campaignDate,
        recipient_display: this.formatRecipientDisplay(row?.scope, row?.recipients_count)
      }
    },
    applyCampaignFilter () {
      const rows = Array.isArray(this.campaignRows) ? this.campaignRows : []
      this.gridData = rows.filter(row => {
        return this.campaignStatusFilter === 'all' || row.status === this.campaignStatusFilter
      })

      if (this.gridApi) {
        this.$nextTick(() => {
          this.gridApi.dispatchEvent({ type: 'modelUpdated' })
        })
      }
    },
    formatDateTime (value) {
      if (!value) return ''

      try {
        return format(parseISO(value), 'dd/MM/yyyy HH:mm', { locale: fr })
      } catch (error) {
        return value
      }
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    },
    formatRecipientDisplay (scope, count) {
      const label = scope === 'not_downloaded'
        ? 'Non téléchargé'
        : 'Tous'

      return `${label} (${this.formatNumber(count)})`
    },
    formatTemplateLabel (template) {
      const labels = {
        onboarding: 'Onboarding',
        reminder: 'Relance',
        custom: 'Personnalisé'
      }

      return labels[template] || template || '-'
    },
    formatStatusLabel (status) {
      return status === 'scheduled' ? 'Programmée' : 'Envoyée'
    }
  }
}
</script>

<style scoped>
.tracking-filters {
  width: auto;
  max-width: 100%;
}

.tracking-filter-field {
  min-width: 11rem;
  flex: 1 1 11rem;
  display: flex;
  align-items: center;
}

.tracking-filter-field--wide {
  min-width: 16rem;
  flex: 1.3 1 16rem;
}

.tracking-filter-control {
  width: 100%;
}

.email-space {
  position: relative;
  overflow: hidden;
}

.email-space::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 0.25rem;
  content: '';
  background: #cbd5e1;
}

.email-space--sent::before {
  background: #2563eb;
}

.email-space-header {
  box-sizing: border-box;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.email-overview-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.email-stat-card {
  position: relative;
  overflow: hidden;
  min-height: 6.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  padding: 1rem;
}

.email-stat-card::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 0.25rem;
  content: '';
  background: #cbd5e1;
}

.email-stat-card span {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.email-stat-card strong {
  display: block;
  margin-top: 0.75rem;
  color: #111827;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1;
}

.email-stat-card--sent::before,
.email-stat-card--recipients::before {
  background: #2563eb;
}

.email-stat-card--scheduled::before,
.email-stat-card--clicks::before {
  background: #0f766e;
}

.email-stat-card--sent {
  background: linear-gradient(180deg, #eff6ff 0%, #ffffff 58%);
}

.email-stat-card--scheduled {
  background: linear-gradient(180deg, #f0fdfa 0%, #ffffff 58%);
}

.email-stat-card--recipients {
  background: linear-gradient(180deg, #eef2ff 0%, #ffffff 58%);
}

.email-stat-card--clicks {
  background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 58%);
}

.email-status-filter {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-self: flex-start;
  border: 1px solid #dbeafe;
  border-radius: 9999px;
  background: #f8fafc;
  padding: 0.25rem;
}

.email-status-filter button {
  min-width: 5.25rem;
  border-radius: 9999px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.55rem 0.85rem;
}

.email-status-filter button.is-active {
  background: #2563eb;
  color: #ffffff;
}

@media (max-width: 1280px) {
  .email-overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .email-overview-grid {
    grid-template-columns: 1fr;
  }

  .email-status-filter {
    width: 100%;
    border-radius: 0.75rem;
  }

  .email-status-filter button {
    flex: 1 1 0;
  }
}
</style>
